import { Alert, Box, CircularProgress, Grid, LinearProgress, Paper, Snackbar, Stack, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ViewIcon from '@mui/icons-material/Visibility';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEndpoints } from '../utils/EndpointContext';
import { FileModel } from '../models/FileModel';
import ConfirmDeleteModal from '../components/modals/ConfirmDeleteModal';
import { PlaceholderType, PlaceholderValues } from '../models/PlaceholderValues';
import CreatePlaceholderButton from '../components/forms/CreateButton';
import { EditableContext } from '../contexts/EditableContext';

type Props = {};
type Params = {
    companyCode: string;
    productType: string;
    versionId: string;
};

const typeLabel = (t: PlaceholderType) => PlaceholderType[t];

const PlaceholdersPage: React.FC<Props> = () => {
    const { companyCode, versionId } = useParams<Params>();
    const endPoints = useEndpoints();
    const navigate = useNavigate();

    const itemsEndRef = useRef<null | HTMLDivElement>(null);
    const [itemAdded, setItemAdded] = useState(false);
    const [shouldSave, setShouldSave] = useState(false);

    const [savedData, setSavedData] = useState<FileModel>();
    const [placeholders, setPlaceholders] = useState<PlaceholderValues[]>();

    const [openAlert, setOpenAlert] = useState<boolean>(false);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const existingKeys = useMemo(
        () => new Set((placeholders ?? []).map(p => p.Key ?? "")),
        [placeholders]
    );

    const isEditable = !savedData?.DatePublished;

    useEffect(() => {
        axios
            .get(`${endPoints.questionEditorRead}`, {
                params: {
                    partitionKey: companyCode,
                    rowKey: versionId,
                    downloadData: true,
                },
            })
            .then((response) => {
                setSavedData(response.data);
                setPlaceholders(response.data.Data!.Placeholders);
            });
    }, [companyCode, endPoints.questionEditorRead, versionId]);

    useEffect(() => {
        setIsLoading(true);
        axios.get(endPoints.questionEditorRead, { params: { partitionKey: companyCode, rowKey: versionId, downloadData: true } })
            .then(res => {
                setSavedData(res.data);
                setPlaceholders(res.data.Data!.Placeholders);
            })
            .finally(() => setIsLoading(false));
    }, [companyCode, endPoints.questionEditorRead, versionId]);

    const handleSave = useCallback(async () => {
        if (isSaving) return;
        setIsSaving(true);
        try {
            savedData!.Data!.Placeholders = placeholders!;
            await axios.post(`${endPoints.questionEditorUpdate}`, savedData);
        } catch (e) {
            console.error(e);
        } finally {
            setOpenAlert(true);
            setIsSaving(false);
        }
    }, [endPoints.questionEditorUpdate, isSaving, placeholders, savedData]);

    useEffect(() => {
        if (itemAdded && itemsEndRef.current) {
            itemsEndRef.current.scrollIntoView({ behavior: 'smooth' });
            setItemAdded(false);
        }
        if (itemAdded) {
            handleSave();
        }
    }, [handleSave, itemAdded]);

    useEffect(() => {
        if (shouldSave) {
            handleSave();
            setShouldSave(false);
        }
    }, [handleSave, shouldSave]);

    function ensureUniqueKey(base: string, existing: Set<string>) {
        let newKey = base;
        if (!existing.has(newKey)) return newKey;
        let matchingCount = 2;
        while (existing.has(`${base} (${matchingCount})`)) matchingCount++;
        return `${base} (${matchingCount})`;
    }

    const handleAlertClose = () => setOpenAlert(false);

    const handleAdd = () => {

        const newKey = ensureUniqueKey("placeholder", existingKeys);

        const newItem: PlaceholderValues = {
            Key: newKey,
            Label: 'New Label',
            Type: PlaceholderType.input,
            Nullable: false
        };
        setPlaceholders((prev) => [...(prev ?? []), newItem]);
        setItemAdded(true);
    };

    const handleRemove = (index: number) => {
        setDeleteIndex(index);
        setOpenDeleteModal(true);
    };
    const confirmRemove = () => {
        if (deleteIndex !== null) {
            setPlaceholders((prev) => prev!.filter((_, i) => i !== deleteIndex));
            setShouldSave(true);
            setOpenDeleteModal(false);
        }
    };

    const handleMoveUp = (index: number) => {
        if (!placeholders || index === 0) return;
        setPlaceholders((prev) => {
            const next = [...prev!];
            [next[index], next[index - 1]] = [next[index - 1], next[index]];
            return next;
        });
        setShouldSave(true);
    };

    const handleMoveDown = (index: number) => {
        if (!placeholders || index >= placeholders.length - 1) return;
        setPlaceholders((prev) => {
            const next = [...prev!];
            [next[index], next[index + 1]] = [next[index + 1], next[index]];
            return next;
        });
        setShouldSave(true);
    };

    return (
        <div>
            {/* show a thin bar while saving */}
            {isSaving && <LinearProgress sx={{ mb: 1 }} />}

            {isLoading ? (
                // loading state
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <EditableContext.Provider value={isEditable}>
                    <Grid container spacing={2} justifyContent="flex-end" sx={{ mb: '1em' }}>
                        <Grid item xs={10}></Grid>
                        <CreatePlaceholderButton onClick={handleAdd} disabled={isSaving} />
                    </Grid>

                    <Stack spacing={2}>
                        {placeholders?.map((item, index) => (
                            <Paper key={item.Key ?? index}>
                                <Grid container spacing={2} alignItems="center">
                                    {isEditable &&
                                        <Grid item xs={1}>
                                            <Stack direction="column" alignItems="center" spacing={0}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleMoveUp(index)}
                                                    disabled={isSaving || index === 0}
                                                    color="primary"
                                                >
                                                    <KeyboardArrowUpIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleMoveDown(index)}
                                                    disabled={isSaving || index === placeholders.length - 1}
                                                    color="primary"
                                                >
                                                    <KeyboardArrowDownIcon />
                                                </IconButton>
                                            </Stack>
                                        </Grid>
                                    }

                                    <Grid item xs={3}>
                                        <Typography>{item.Key}</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography>{item.Label}</Typography>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Typography>{typeLabel(item.Type)}</Typography>
                                    </Grid>

                                    <Grid item xs={2} className="actions-bar tools" display="flex" justifyContent="flex-end">
                                        {isEditable
                                         ? <>
                                            <Button color="primary" onClick={() => navigate(`${item.Key}`)} disabled={isSaving}>
                                                <EditIcon />
                                            </Button>
                                            <Button color="error" onClick={() => handleRemove(index)} disabled={isSaving}>
                                                <DeleteIcon />
                                            </Button>
                                           </>
                                         :
                                            <Button color="primary" onClick={() => navigate(`${item.Key}`)} disabled={isSaving}>
                                                <ViewIcon />
                                            </Button>
                                        }
                                    </Grid>
                                </Grid>
                            </Paper>
                        ))}
                    </Stack>

                    <div ref={itemsEndRef} />

                    <Grid container spacing={2} justifyContent="flex-end" sx={{ mt: '1em' }}>
                        <Grid item xs={10}></Grid>
                        <CreatePlaceholderButton onClick={handleAdd} disabled={isSaving} />
                    </Grid>

                    <Snackbar open={openAlert} autoHideDuration={6000} onClose={handleAlertClose}>
                        <Alert severity="success" sx={{ width: '100%' }}>
                            Placeholders updated successfully.
                        </Alert>
                    </Snackbar>

                    <ConfirmDeleteModal
                        open={openDeleteModal}
                        onClose={() => setOpenDeleteModal(false)}
                        onConfirm={confirmRemove}
                    />
                </EditableContext.Provider>
            )}
        </div>
    );
};

export default PlaceholdersPage;